import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { makeSun } from './look/sun.js';

/**
 * The studio: a calm room for lessons without a world (ADR-030; the calm look of ADR-098).
 *
 * A generated VR lesson about a volcano had one place to stand, the solar
 * system's starfield, and a volcano floating among the stars teaches
 * something nobody meant. This is the plain alternative, and since ADR-098 the
 * room of every close-up lesson ("Eén ding van dichtbij"): a soft gradient from
 * a warm pale horizon to a quiet blue overhead, a floor that is lightest where
 * the model stands and fades into the sky, a few faint rings on it to judge
 * sizes and distances by, and the light from the side so a model's shape
 * stands out. Calm on purpose: the model is the subject. Built in code from a
 * handful of vertex-coloured meshes, with no textures, no media and no
 * post-processing, so it costs next to nothing to draw on a Quest.
 *
 * The floor is at y = 0, which in a `local-floor` session is the real floor
 * under the student's feet: standing on it is what keeps a room comfortable.
 */

/** Where the floor meets the sky. Also the stage's background colour for this scene. */
export const STUDIO_HORIZON = 0xe4e2dd;
const STUDIO_ZENITH = 0xa7b9cc;
/** The floor under the model and the pupil: a touch lighter than the floor around it. */
const FLOOR_CENTRE = 0xd9d6d0;
const FLOOR_NEAR = 0xcbc8c2;

export const STUDIO_FLOOR_RADIUS = 40;
/** Inside the camera's far plane (300 m, stage.ts), or the sky would be clipped away. */
export const STUDIO_SKY_RADIUS = 150;

/**
 * The faint rings on the floor (ADR-098): one a metre out to 6 m round the
 * middle, 2 cm wide, each fainter than the last. Opaque and drawn in the
 * floor's own colour, only a little darker -- no transparency to sort, one
 * draw for all of them.
 */
export const STUDIO_RINGS = { every: 1, out: 6, width: 0.02, darkest: 0.09 } as const;

/** Where the key light comes from: the side and a little in front, above (ADR-098). */
export const STUDIO_KEY_FROM: readonly [number, number, number] = [5, 3.5, 1.5];

export type Studio = { root: THREE.Group; lights: THREE.Light[] };

/** The shadow disc's reach and strength on the unlit floor (ADR-032). */
export const STUDIO_SHADOW = { radius: 8, opacity: 0.22 } as const;

/**
 * The studio's lights. The better look (ADR-032, the default): the key light
 * from the side casts the one soft shadow onto a shadow-catching disc on the
 * floor, a cooler fill from the other side keeps the shaded side readable, and
 * the sky light is a PMREM of three's `RoomEnvironment` (set by look.ts, since
 * it needs the renderer) instead of the hemisphere light. `flat` is the look
 * before worlds, for `?look=flat`.
 */
export function buildStudio(options: { flat?: boolean } = {}): Studio {
  const root = new THREE.Group();
  root.name = 'studio';
  root.add(floor(), rings(), sky());

  const keyFrom = new THREE.Vector3(...STUDIO_KEY_FROM).normalize();
  // Lower and from the other side, cooler: the shaded side of a model is
  // dim, never black.
  const fill = new THREE.DirectionalLight(0xdfe8ff, 0.55);
  fill.position.set(-4, 2, 2);

  if (options.flat) {
    // Soft all round.
    const ambient = new THREE.HemisphereLight(0xffffff, 0x9aa0a8, 1.3);
    const key = new THREE.DirectionalLight(0xffffff, 1.6);
    key.position.copy(keyFrom).multiplyScalar(7);
    return { root, lights: [ambient, key, fill] };
  }

  const key = makeSun(keyFrom, 0xfff6ea, 2.1);
  root.add(key.target, shadowCatcher());
  return { root, lights: [key, fill] };
}

/**
 * Catches the key light's shadow on the unlit floor and shows only that: a
 * lit floor and an unlit sky never meet in one colour, so the floor stays
 * unlit and fades into the sky as before.
 */
function shadowCatcher(): THREE.Mesh {
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(STUDIO_SHADOW.radius, 64),
    new THREE.ShadowMaterial({ opacity: STUDIO_SHADOW.opacity, depthWrite: false }),
  );
  disc.name = 'studio-shadow';
  disc.rotation.x = -Math.PI / 2;
  // Above the floor and the grid, so neither fights it for depth.
  disc.position.y = 0.004;
  disc.receiveShadow = true;
  disc.raycast = (): void => {};
  return disc;
}

/** The floor's colour `r` metres from the middle: lightest under the model, the sky's at its edge. */
export function floorColour(r: number): THREE.Color {
  const centre = new THREE.Color(FLOOR_CENTRE);
  const near = new THREE.Color(FLOOR_NEAR);
  const horizon = new THREE.Color(STUDIO_HORIZON);
  if (r <= 6) return centre.lerp(near, THREE.MathUtils.smoothstep(r, 1.5, 6));
  return near.lerp(horizon, THREE.MathUtils.smoothstep(r, 6, STUDIO_FLOOR_RADIUS));
}

/** Colours each vertex by its distance from the middle on the floor's plane. */
function shadeFloor(geometry: THREE.BufferGeometry, colourAt: (r: number) => THREE.Color): void {
  const position = geometry.getAttribute('position');
  const colours = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i++) {
    const colour = colourAt(Math.hypot(position.getX(i), position.getZ(i)));
    colours.set([colour.r, colour.g, colour.b], i * 3);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
}

/**
 * Lightest near the student and the sky's own colour at the edge, so there is
 * no line where the floor stops. Unlit, like the sky: a lit floor and an
 * unlit sky never meet in the same colour.
 */
function floor(): THREE.Mesh {
  // A ring with many rings of vertices rather than a circle: a circle has
  // only its centre and its rim, and the fade would be one straight ramp.
  const geometry = new THREE.RingGeometry(0, STUDIO_FLOOR_RADIUS, 96, 24);
  geometry.rotateX(-Math.PI / 2);
  shadeFloor(geometry, floorColour);
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false }),
  );
  mesh.name = 'studio-floor';
  return mesh;
}

/**
 * Faint rings a metre apart near the student: something to judge sizes and
 * distances by, quieter than a grid's lines and without their shimmer.
 */
function rings(): THREE.Mesh {
  const parts: THREE.BufferGeometry[] = [];
  const { every, out, width, darkest } = STUDIO_RINGS;
  for (let r = every; r <= out + 1e-9; r += every) {
    const ring = new THREE.RingGeometry(r - width / 2, r + width / 2, 160, 1);
    ring.rotateX(-Math.PI / 2);
    // The floor's own colour there, a little darker; the outer rings fainter.
    const shade = darkest * (1 - (0.6 * (r - every)) / out);
    shadeFloor(ring, (at) => floorColour(at).multiplyScalar(1 - shade));
    parts.push(ring);
  }
  const geometry = mergeGeometries(parts, false);
  for (const part of parts) part.dispose();
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false, depthWrite: false }),
  );
  mesh.name = 'studio-rings';
  // Just above the floor, or the two fight over the same depth.
  mesh.position.y = 0.002;
  mesh.raycast = (): void => {};
  return mesh;
}

/** Colours each vertex by how far along `t(x, y, z)` puts it between two colours. */
function shade(
  geometry: THREE.BufferGeometry,
  from: number,
  to: number,
  t: (x: number, y: number, z: number) => number,
): void {
  const a = new THREE.Color(from);
  const b = new THREE.Color(to);
  const colour = new THREE.Color();
  const position = geometry.getAttribute('position');
  const colours = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i++) {
    colour.lerpColors(a, b, t(position.getX(i), position.getY(i), position.getZ(i)));
    colours.set([colour.r, colour.g, colour.b], i * 3);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
}

function sky(): THREE.Mesh {
  const geometry = new THREE.SphereGeometry(STUDIO_SKY_RADIUS, 32, 16);
  shade(geometry, STUDIO_HORIZON, STUDIO_ZENITH, (_x, y) =>
    THREE.MathUtils.smoothstep(y / STUDIO_SKY_RADIUS, 0.02, 0.8),
  );
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      vertexColors: true,
      side: THREE.BackSide,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  mesh.name = 'studio-sky';
  // Behind everything, whatever the distances say.
  mesh.renderOrder = -1;
  return mesh;
}
